import { z } from 'zod'
import { positiveMoneyCodec } from './exchange'
import { deviceIdSchema, isoDate } from './trip'
import { exchangeDaySchema } from '#model/entities/exchange'
import { spendingTextSchema } from '#model/entities/spending'
import { ISSUE } from '#model/support/errors'
import {
  spendingCategoryColourSchema,
  spendingCategoryNameSchema,
  spendingIconSchema,
  spendingPresetSchema,
} from '#model/entities/spending-category'
import type { SpendingCategory } from '#model/entities/spending-category'
import { moneyCodec } from '#model/values/money'
import { rateCodec } from '#model/values/rates'

/** What a spending says, as the screen sends it — the same whether it is recorded or amended. */
const spendingFields = {
  spentOn: exchangeDaySchema,
  amount: positiveMoneyCodec,
  // A reference, not a name the device gives: read in either case, as an address is (MOL-25, Р-3),
  // or `AB12…` of one's own category is «unknown» against the `ab12…` the server answered with.
  categoryId: z.uuid().overwrite((id) => id.toLowerCase()),
  note: spendingTextSchema.optional(),
  place: spendingTextSchema.optional(),
  /**
   * The account it was paid from (MOL-115). Unlike every other field, one left out of an amendment
   * is **kept**, not cleared: a screen older than accounts amends a note and must not take the
   * spending off its account in silence (Р-26). `null` is «без счёта».
   */
  accountId: z
    .uuid()
    .overwrite((id) => id.toLowerCase())
    .nullable()
    .optional(),
  /** «Списано со счёта», in the account's currency (MOL-43 В-3); left out, it follows the account. */
  debited: positiveMoneyCodec.nullable().optional(),
}

function withDebitedRule<
  Schema extends z.ZodType<{ accountId?: string | null | undefined; debited?: unknown }>,
>(schema: Schema) {
  return schema.refine(({ accountId, debited }) => debited == null || accountId !== null, {
    error: ISSUE.DEBITED_WITHOUT_ACCOUNT,
    path: ['debited'],
  })
}

/**
 * «Сохранить» a new spending (MOL-73). Named by the device, as an exchange is, so a spending sent
 * twice from the queue is one spending. «Not after today» is the use case's, which has the clock.
 */
export const spendingBodySchema = withDebitedRule(
  z.strictObject({ id: deviceIdSchema, ...spendingFields }),
)
export type SpendingBody = z.infer<typeof spendingBodySchema>

/**
 * «Сохранить» an amended one: the spending whole as it should now be, and the version it was amended
 * over — one amended on another phone in between is a conflict rather than lost (MOL-42, В-3).
 */
export const spendingAmendBodySchema = withDebitedRule(
  z.strictObject({
    revision: z.int().min(1),
    ...spendingFields,
  }),
)
export type SpendingAmendBody = z.infer<typeof spendingAmendBodySchema>

/** One spending as the screen shows it, with the rate of its own day when it is in another currency. */
export const spendingViewCodec = z.strictObject({
  id: z.uuid(),
  spentOn: exchangeDaySchema,
  amount: moneyCodec,
  categoryId: z.uuid(),
  note: z.string().nullable(),
  place: z.string().nullable(),
  rate: rateCodec.nullable(),
  // Defaults, so an answer of a server older than accounts still reads (MOL-115).
  accountId: z.uuid().nullable().default(null),
  debited: moneyCodec.nullable().default(null),
  /** The transfer it is the fee of (MOL-253): its row opens the transfer's sheet, not a spending's. */
  transferId: z.uuid().nullable().default(null),
  revision: z.int().min(1),
  amendedAt: isoDate.nullable(),
})
export type SpendingView = z.output<typeof spendingViewCodec>

/**
 * «Добавить категорию»: one's own, named by the device, as everything written offline is. The icon
 * and the colour are chosen (MOL-285); a body queued by an earlier build has neither, and takes the
 * tag and the palette's next.
 */
export const spendingCategoryBodySchema = z.strictObject({
  id: deviceIdSchema,
  name: spendingCategoryNameSchema,
  icon: spendingIconSchema.optional(),
  colour: spendingCategoryColourSchema.optional(),
})
export type SpendingCategoryBody = z.infer<typeof spendingCategoryBodySchema>

/**
 * A category of one's own amended (MOL-285, В-1 «а»): its name, icon and colour, whole — sent again,
 * it is the same state, so a repeat from the queue needs no revision.
 */
export const spendingCategoryAmendSchema = z.strictObject({
  name: spendingCategoryNameSchema,
  icon: spendingIconSchema,
  colour: spendingCategoryColourSchema,
})
export type SpendingCategoryAmend = z.infer<typeof spendingCategoryAmendSchema>

/**
 * A category as the screen names it: a preset by its key — the screen says it in its language —
 * one's own by its name, a colour of the palette and an icon of the set. `archived` is «taken out of
 * the choice»: still the category of the spendings in it, still in their months, offered back in the
 * list.
 */
export const spendingCategoryViewCodec = z.strictObject({
  id: z.uuid(),
  preset: spendingPresetSchema.nullable(),
  name: z.string().nullable(),
  colour: spendingCategoryColourSchema.nullable(),
  // A default, so an answer the phone kept from before MOL-285 still reads: one's own is the tag.
  icon: spendingIconSchema.nullable().default(null),
  archived: z.boolean(),
})
export type SpendingCategoryView = z.output<typeof spendingCategoryViewCodec>

export function spendingCategoryViewOf(category: SpendingCategory): SpendingCategoryView {
  return {
    id: category.id,
    preset: category.preset,
    name: category.name,
    colour: category.colour,
    icon: category.icon,
    archived: category.archivedAt !== null,
  }
}

/**
 * The ids the chips of a spending stand first by (MOL-198): `frequentCategories`, the rest after them
 * in the list's order. A default, so an answer the phone kept from before it still reads — as a
 * newcomer's, the presets as listed.
 */
export const frequentCategoriesCodec = z.array(z.uuid()).default([])

/** The person's categories in the order of the list, the removed ones included and marked. */
export const spendingCategoriesResponseCodec = z.strictObject({
  categories: z.array(spendingCategoryViewCodec),
  frequent: frequentCategoriesCodec,
})
export type SpendingCategoriesResponse = z.output<typeof spendingCategoriesResponseCodec>
